import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, ChevronRight, Star, CheckCircle, Shield, Award, Clock,
  ArrowRight, MapPin, Droplets, Wrench, Home, Building2,
  AlertTriangle, Search, Waves, ChevronDown, ChevronUp,
} from 'lucide-react';
import { APP_CONFIG, ROUTES } from '../../config/app.config';
import { MOCK_TESTIMONIALS, MOCK_FAQS } from '../../mock';
import { MOCK_SERVICES } from '../../mock/services.mock';

const stats = [
  { label: 'Roofs Completed', value: '3,200+', icon: '🏠' },
  { label: 'Happy Customers', value: '2,900+', icon: '⭐' },
  { label: 'Years in Business', value: '21+', icon: '📅' },
  { label: 'Service Areas', value: '12+', icon: '📍' },
];

const certifications = [
  { name: 'GAF Master Elite', sub: 'Top 3% of Contractors', icon: '🏆' },
  { name: 'CertainTeed SELECT', sub: 'ShingleMaster™', icon: '🛡️' },
  { name: 'BBB A+', sub: 'Accredited Business', icon: '✅' },
  { name: 'NRCA Member', sub: 'National Roofing Contractors', icon: '🏗️' },
];

const serviceIcons: Record<string, React.ComponentType<any>> = {
  'roof-cleaning': Droplets,
  'roof-repair': Wrench,
  'roof-replacement': Home,
  'commercial': Building2,
  'emergency': AlertTriangle,
  'inspection': Search,
  'gutter-cleaning': Waves,
};

const ELO_GOLD = '#E09900';
const ELO_CTA  = '#FA873D';
const ELO_NAVY = '#1E1E57';
const ELO_DARK = '#0A0A1E';

const HomePage: React.FC = () => {
  const [activeFaq, setActiveFaq] = useState<string | null>(null);

  return (
    <div>
      {/* ─── Hero — Video BG (Elo-style) ───────────────────────── */}
      <section style={{
        minHeight: '100vh', position: 'relative', display: 'flex', alignItems: 'center',
        background: ELO_DARK, overflow: 'hidden',
      }}>
        {/* Video Background */}
        <video
          autoPlay muted loop playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.35 }}
        >
          <source src="https://eloroofing.com/wp-content/uploads/2024/06/elo-roof-v2.mp4" type="video/mp4" />
          {/* Fallback image if video fails */}
          <img
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1800"
            alt="Roofing"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </video>

        {/* Gradient overlay — navy to indigo */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(135deg, rgba(10,10,30,0.92) 0%, rgba(30,30,87,0.82) 55%, rgba(43,43,125,0.70) 100%)`,
        }} />

        {/* Gold shimmer glow */}
        <div style={{
          position: 'absolute', top: '15%', right: '8%',
          width: '500px', height: '500px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(224,153,0,0.18) 0%, rgba(250,135,61,0.08) 50%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div className="container-xl" style={{ position: 'relative', zIndex: 1, padding: '8.5rem 1.5rem 5rem', width: '100%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div>
              {/* Badge */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: 'rgba(224,153,0,0.14)', border: '1px solid rgba(224,153,0,0.35)',
                borderRadius: '9999px', padding: '0.375rem 1.1rem', marginBottom: '1.5rem',
              }}>
                <Award size={14} color={ELO_GOLD} />
                <span style={{ color: ELO_GOLD, fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  GAF Master Elite Certified · Nashville's #1 Rated
                </span>
              </div>

              <h1 style={{
                color: 'white', fontSize: 'clamp(2.2rem, 4vw, 3.6rem)',
                fontWeight: 900, lineHeight: 1.1, marginBottom: '1.25rem',
                textTransform: 'uppercase', letterSpacing: '-0.5px',
              }}>
                Nashville's Most{' '}
                <span className="gradient-text">Trusted</span>{' '}
                Roofing Company
              </h1>

              <p style={{ color: '#c5d9f2', fontSize: '1.05rem', lineHeight: 1.7, marginBottom: '2rem', maxWidth: '480px' }}>
                Professional roof cleaning, repair, replacement, and commercial services.
                GAF Master Elite certified. 10-year workmanship warranty. Free estimates — always.
              </p>

              {/* CTA Buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '2.5rem' }}>
                <Link to={ROUTES.QUOTE} className="btn-primary" style={{ fontSize: '0.95rem' }}>
                  Get Free Estimate <ArrowRight size={16} />
                </Link>
                <a href={`tel:${APP_CONFIG.phoneRaw}`} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.75rem 1.75rem', borderRadius: '100px', fontWeight: 700,
                  fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.04em',
                  border: '2px solid rgba(255,255,255,0.4)', color: 'white',
                  textDecoration: 'none', transition: 'all 0.25s ease',
                  fontFamily: "'Roboto', sans-serif",
                }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = ELO_GOLD; (e.currentTarget as HTMLElement).style.color = ELO_GOLD; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.4)'; (e.currentTarget as HTMLElement).style.color = 'white'; }}
                >
                  <Phone size={16} /> {APP_CONFIG.phone}
                </a>
              </div>

              {/* Trust Signals */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
                {[
                  { icon: <Star size={14} fill={ELO_GOLD} stroke="none" />, text: '4.9/5 — 500+ reviews' },
                  { icon: <Shield size={14} color="#10b981" />, text: '10-Year Warranty' },
                  { icon: <Clock size={14} color="#60a5fa" />, text: '24/7 Emergency' },
                ].map(item => (
                  <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    {item.icon}
                    <span style={{ color: '#c5d9f2', fontSize: '0.85rem', fontWeight: 500 }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Quote Card */}
            <div className="hidden-mobile">
              <div style={{
                background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1.5rem', padding: '2rem',
                boxShadow: '0 24px 64px rgba(10,10,30,0.5)',
              }}>
                {/* Gold top bar */}
                <div style={{ height: '4px', background: `linear-gradient(90deg, ${ELO_CTA}, ${ELO_GOLD})`, borderRadius: '2px', marginBottom: '1.5rem' }} />
                <h3 style={{ color: 'white', fontWeight: 800, marginBottom: '1.25rem', fontSize: '1.1rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Get Your Free Quote
                </h3>
                <form style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                  <input className="input-field" placeholder="Your Full Name" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }} />
                  <input className="input-field" placeholder="Phone Number" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }} />
                  <input className="input-field" placeholder="Property Address" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }} />
                  <select className="input-field" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }}>
                    <option value="">Select Service...</option>
                    <option>Roof Cleaning</option>
                    <option>Roof Repair</option>
                    <option>Roof Replacement</option>
                    <option>Commercial Roofing</option>
                    <option>Emergency Service</option>
                    <option>Roof Inspection</option>
                  </select>
                  <Link to={ROUTES.QUOTE} className="btn-primary" style={{ textAlign: 'center', marginTop: '0.25rem' }}>
                    Request Free Estimate
                  </Link>
                </form>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', textAlign: 'center', marginTop: '0.875rem' }}>
                  No obligation. Typically respond within 2 hours.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', animation: 'bounce 2s infinite' }}>
          <ChevronDown size={24} color="rgba(255,255,255,0.4)" />
        </div>
        <style>{`
          @keyframes bounce { 0%,100%{transform:translateX(-50%) translateY(0)} 50%{transform:translateX(-50%) translateY(-8px)} }
          @media (max-width: 900px) { .hidden-mobile{display:none!important} }
        `}</style>
      </section>

      {/* ─── Stats Bar ─────────────────────────────────────────── */}
      <section style={{ background: ELO_NAVY, padding: '2.25rem 1.5rem' }}>
        <div className="container-xl">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '2rem', textAlign: 'center' }}>
            {stats.map((s, i) => (
              <div key={s.label} className={`reveal reveal-delay-${i + 1}`}>
                <div style={{ fontSize: '1.85rem', fontWeight: 900, color: 'white', marginBottom: '0.25rem' }}>
                  <span style={{ marginRight: '0.25rem' }}>{s.icon}</span>{s.value}
                </div>
                <div style={{ color: '#9ab1e8', fontSize: '0.85rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Services ──────────────────────────────────────────── */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }} className="reveal">
            <span style={{ color: ELO_GOLD, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>What We Do</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: ELO_NAVY, textTransform: 'uppercase' }}>
              Complete Roofing Solutions
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '1rem', maxWidth: '540px', margin: '0.75rem auto 0' }}>
              From a single shingle to full commercial installations — we handle every roofing need with expertise and precision.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {MOCK_SERVICES.map((service, i) => {
              const IconComp = serviceIcons[service.category] || Home;
              return (
                <Link key={service.id} to={`/services/${service.slug}`} style={{ textDecoration: 'none' }}
                  className={`reveal-scale reveal-delay-${Math.min(i + 1, 5)}`}>
                  <div className="card card-hover" style={{ height: '100%', cursor: 'pointer' }}>
                    {service.popular && (
                      <div style={{ marginBottom: '1rem' }}>
                        <span className="badge badge-orange">Most Popular</span>
                      </div>
                    )}
                    <div style={{
                      width: '3.5rem', height: '3.5rem', borderRadius: '0.875rem',
                      background: `linear-gradient(135deg, rgba(224,153,0,0.15), rgba(198,144,43,0.08))`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem',
                    }}>
                      <IconComp size={26} color={ELO_GOLD} />
                    </div>
                    <h3 style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.5rem', color: ELO_NAVY }}>{service.title}</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>{service.shortDesc}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Starting from</div>
                        <div style={{ fontWeight: 800, color: ELO_GOLD, fontSize: '1.1rem' }}>{service.price}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: ELO_CTA, fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Learn More <ChevronRight size={14} />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Why Choose Us ─────────────────────────────────────── */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container-xl">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div className="reveal-left">
              <span style={{ color: ELO_GOLD, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Why Cates Roofing</span>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', marginBottom: '1.25rem', color: ELO_NAVY, textTransform: 'uppercase' }}>
                The Roofing Company That Treats You Like Family
              </h2>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: '2rem', fontSize: '1rem' }}>
                For over 21 years, Cates Roofing has served Nashville and surrounding communities with integrity, craftsmanship, and unmatched warranties. We're not satisfied until you are.
              </p>
              {[
                { icon: <Shield size={20} color={ELO_GOLD} />, title: '10-Year Workmanship Warranty', desc: 'The most comprehensive warranty in the region. We stand behind every nail.' },
                { icon: <Award size={20} color={ELO_GOLD} />, title: 'GAF Master Elite Certified', desc: 'Only 3% of roofing contractors in America earn this distinction.' },
                { icon: <CheckCircle size={20} color={ELO_GOLD} />, title: 'Licensed, Bonded & Insured', desc: "Fully covered so you're fully protected. We never cut corners." },
                { icon: <Clock size={20} color={ELO_GOLD} />, title: 'On-Time, Every Time', desc: 'We respect your schedule. Jobs start when promised and finish on deadline.' },
              ].map(item => (
                <div key={item.title} style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem' }}>
                  <div style={{
                    width: '2.5rem', height: '2.5rem', borderRadius: '0.625rem',
                    background: 'rgba(224,153,0,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {item.icon}
                  </div>
                  <div>
                    <h4 style={{ fontWeight: 700, color: ELO_NAVY, marginBottom: '0.25rem', fontSize: '0.95rem' }}>{item.title}</h4>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0, lineHeight: 1.6 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
              <Link to={ROUTES.ABOUT} className="btn-primary" style={{ marginTop: '0.5rem' }}>
                Learn Our Story <ArrowRight size={16} />
              </Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }} className="reveal-right">
              <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=500&auto=format&fit=crop&q=80" alt="Roof repair" style={{ borderRadius: '1rem', objectFit: 'cover', height: '240px', width: '100%' }} onError={e => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=500'; }} />
              <img src="https://images.unsplash.com/photo-1613977257363-707ba9348227?w=500&auto=format&fit=crop&q=80" alt="New roof" style={{ borderRadius: '1rem', objectFit: 'cover', height: '240px', width: '100%', marginTop: '2rem' }} onError={e => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=500'; }} />
              <img src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=500&auto=format&fit=crop&q=80" alt="Commercial" style={{ borderRadius: '1rem', objectFit: 'cover', height: '240px', width: '100%', marginTop: '-2rem' }} onError={e => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=500'; }} />
              <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=500&auto=format&fit=crop&q=80" alt="Cleaning" style={{ borderRadius: '1rem', objectFit: 'cover', height: '240px', width: '100%' }} onError={e => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=500'; }} />
            </div>
          </div>
        </div>
        <style>{`@media(max-width:900px){ div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important} }`}</style>
      </section>

      {/* ─── Certifications ─────────────────────────────────────── */}
      <section style={{ background: ELO_DARK, padding: '3.5rem 1.5rem' }}>
        <div className="container-xl">
          <p style={{ textAlign: 'center', color: '#9ab1e8', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '2rem' }} className="reveal">
            Certifications & Memberships
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {certifications.map((cert, i) => (
              <div key={cert.name}
                className={`reveal-scale reveal-delay-${i + 1}`}
                style={{
                  textAlign: 'center', padding: '1.75rem 1rem',
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '1rem',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px) scale(1.03)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 32px rgba(224,153,0,0.2)`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{cert.icon}</div>
                <div style={{ color: 'white', fontWeight: 700, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{cert.name}</div>
                <div style={{ color: '#9ab1e8', fontSize: '0.75rem', marginTop: '0.25rem' }}>{cert.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Before/After Gallery ──────────────────────────────── */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }} className="reveal">
            <span style={{ color: ELO_GOLD, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Our Work</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: ELO_NAVY, textTransform: 'uppercase' }}>
              Before & After Transformations
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {[
              { before: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600', after: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=600', title: 'Full Replacement — Nashville', type: 'Roof Replacement' },
              { before: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600', after: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?w=600', title: 'Storm Damage Repair — Brentwood', type: 'Roof Repair' },
              { before: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600', after: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600', title: 'Soft-Wash Cleaning — Franklin', type: 'Roof Cleaning' },
            ].map((item, i) => (
              <div key={item.title} className={`card reveal-scale reveal-delay-${i + 1}`} style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '200px' }}>
                  <div style={{ position: 'relative', overflow: 'hidden' }}>
                    <img src={item.before} alt="Before" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', bottom: '0.5rem', left: '0.5rem', background: 'rgba(0,0,0,0.75)', color: 'white', padding: '0.25rem 0.625rem', borderRadius: '0.375rem', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>Before</div>
                  </div>
                  <div style={{ position: 'relative', overflow: 'hidden' }}>
                    <img src={item.after} alt="After" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', bottom: '0.5rem', right: '0.5rem', background: '#16a34a', color: 'white', padding: '0.25rem 0.625rem', borderRadius: '0.375rem', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>After</div>
                  </div>
                </div>
                <div style={{ padding: '1rem 1.25rem' }}>
                  <div style={{ fontSize: '0.7rem', color: ELO_GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{item.type}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: ELO_NAVY, marginTop: '0.25rem' }}>{item.title}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to={ROUTES.GALLERY} className="btn-secondary">View Full Gallery <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* ─── Testimonials ──────────────────────────────────────── */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }} className="reveal">
            <span style={{ color: ELO_GOLD, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Customer Reviews</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: ELO_NAVY, textTransform: 'uppercase' }}>
              What Our Customers Say
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem', marginTop: '0.75rem' }}>
              {[1,2,3,4,5].map(i => <Star key={i} size={22} fill={ELO_GOLD} stroke="none" />)}
              <span style={{ fontWeight: 800, color: ELO_NAVY, marginLeft: '0.5rem', fontSize: '1.1rem' }}>4.9</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>· 500+ verified reviews</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {MOCK_TESTIMONIALS.slice(0, 3).map((t, i) => (
              <div key={t.id} className={`card card-hover reveal reveal-delay-${i + 1}`}>
                <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '1rem' }}>
                  {Array.from({ length: t.rating }).map((_, j) => <Star key={j} size={16} fill={ELO_GOLD} stroke="none" />)}
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '1.25rem', fontStyle: 'italic' }}>
                  "{t.text}"
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {t.avatar && <img src={t.avatar} alt={t.name} style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', objectFit: 'cover' }} />}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.875rem', color: ELO_NAVY }}>{t.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      <MapPin size={11} /> {t.location} · {t.service}
                    </div>
                  </div>
                  {t.verified && <div style={{ marginLeft: 'auto' }}><CheckCircle size={16} color="#10b981" /></div>}
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to={ROUTES.TESTIMONIALS} className="btn-secondary">Read All Reviews <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* ─── Process ───────────────────────────────────────────── */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container-xl">
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }} className="reveal">
            <span style={{ color: ELO_GOLD, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Simple Process</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: ELO_NAVY, textTransform: 'uppercase' }}>
              How It Works
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '2rem' }}>
            {[
              { step: '01', title: 'Request a Quote', desc: 'Fill out our quick online form or call us. We respond within 2 hours.', icon: '📋' },
              { step: '02', title: 'Free Inspection', desc: 'A licensed inspector visits your property and provides a detailed assessment.', icon: '🔍' },
              { step: '03', title: 'Review Proposal', desc: 'Receive a clear, itemized proposal. No surprise fees. No pressure.', icon: '📄' },
              { step: '04', title: 'We Get to Work', desc: 'Our certified crew completes the job on schedule, to the highest standard.', icon: '🏗️' },
              { step: '05', title: 'Final Walkthrough', desc: 'We walk you through the completed work and you approve before we leave.', icon: '✅' },
            ].map((step, i) => (
              <div key={step.step} style={{ textAlign: 'center' }} className={`reveal-scale reveal-delay-${i + 1}`}>
                <div style={{
                  width: '4.5rem', height: '4.5rem', borderRadius: '50%',
                  background: `linear-gradient(135deg, ${ELO_CTA}, ${ELO_GOLD})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 1.25rem', fontSize: '1.75rem',
                  boxShadow: `0 8px 24px rgba(224,153,0,0.35)`,
                }}>
                  {step.icon}
                </div>
                <div style={{ color: ELO_GOLD, fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                  Step {step.step}
                </div>
                <h3 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.5rem', color: ELO_NAVY }}>{step.title}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.6 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ───────────────────────────────────────────────── */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container-xl" style={{ maxWidth: '720px' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }} className="reveal">
            <span style={{ color: ELO_GOLD, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>FAQ</span>
            <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: ELO_NAVY, textTransform: 'uppercase' }}>
              Common Questions
            </h2>
          </div>
          {MOCK_FAQS.slice(0, 5).map((faq, i) => (
            <div key={faq.id} className={`card reveal reveal-delay-${Math.min(i + 1, 5)}`} style={{ marginBottom: '0.875rem', padding: '0', borderRadius: '1.25rem', overflow: 'hidden' }}>
              <button
                onClick={() => setActiveFaq(activeFaq === faq.id ? null : faq.id)}
                style={{
                  width: '100%', padding: '1.25rem 1.5rem',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: activeFaq === faq.id ? `rgba(224,153,0,0.06)` : 'none',
                  border: 'none', cursor: 'pointer', textAlign: 'left', gap: '1rem',
                  transition: 'background 0.2s ease',
                }}>
                <span style={{ fontWeight: 600, fontSize: '0.95rem', color: ELO_NAVY }}>{faq.question}</span>
                {activeFaq === faq.id
                  ? <ChevronUp size={18} style={{ color: ELO_GOLD, flexShrink: 0 }} />
                  : <ChevronDown size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
              </button>
              {activeFaq === faq.id && (
                <div style={{ padding: '0 1.5rem 1.25rem' }} className="fade-in">
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to={ROUTES.FAQ} className="btn-secondary">View All FAQs <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* ─── Service Areas ──────────────────────────────────────── */}
      <section style={{ padding: '3rem 1.5rem', background: 'var(--bg-primary)' }}>
        <div className="container-xl" style={{ textAlign: 'center' }}>
          <h3 style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '1rem', color: ELO_NAVY, textTransform: 'uppercase', letterSpacing: '0.08em' }} className="reveal">
            Proudly Serving
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.625rem', justifyContent: 'center' }}>
            {['Nashville', 'Franklin', 'Brentwood', 'Murfreesboro', 'Smyrna', 'Hendersonville', 'Gallatin', 'Mount Juliet', 'Lebanon', 'Antioch', 'Nolensville', 'Spring Hill'].map(city => (
              <div key={city} style={{
                display: 'flex', alignItems: 'center', gap: '0.25rem',
                background: 'var(--bg-secondary)', border: `1px solid rgba(30,30,87,0.12)`,
                borderRadius: '9999px', padding: '0.375rem 0.875rem',
                fontSize: '0.8rem', color: ELO_NAVY, fontWeight: 500,
                transition: 'all 0.2s ease',
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = ELO_NAVY; (e.currentTarget as HTMLElement).style.color = 'white'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-secondary)'; (e.currentTarget as HTMLElement).style.color = ELO_NAVY; }}
              >
                <MapPin size={12} color={ELO_GOLD} /> {city}, TN
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Emergency CTA ──────────────────────────────────────── */}
      <section style={{ background: ELO_DARK, padding: '4.5rem 1.5rem' }}>
        <div className="container-xl" style={{ textAlign: 'center' }}>
          <div style={{
            background: `linear-gradient(135deg, rgba(224,153,0,0.1), rgba(250,135,61,0.07))`,
            border: '1px solid rgba(224,153,0,0.25)', borderRadius: '1.5rem',
            padding: '3.5rem 2rem', maxWidth: '660px', margin: '0 auto',
          }} className="reveal-scale">
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🚨</div>
            <h2 style={{ color: 'white', fontWeight: 900, fontSize: '1.75rem', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '-0.5px' }}>
              Roof Emergency?
            </h2>
            <p style={{ color: '#9ab1e8', marginBottom: '2rem', fontSize: '1rem', maxWidth: '480px', margin: '0 auto 2rem' }}>
              We respond 24 hours a day, 7 days a week. Average response time: under 2 hours. Don't wait — water damage compounds every hour.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
              <a href={`tel:${APP_CONFIG.phoneRaw}`} className="btn-primary" style={{ background: `linear-gradient(135deg, #ef4444, #dc2626)`, boxShadow: '0 4px 20px rgba(239,68,68,0.4)' }}>
                <Phone size={16} /> Call Emergency Line
              </a>
              <Link to="/services/emergency-roofing" style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.75rem 1.75rem', borderRadius: '100px', fontWeight: 700,
                fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.04em',
                border: '2px solid rgba(255,255,255,0.3)', color: 'white',
                textDecoration: 'none', transition: 'all 0.25s ease',
                fontFamily: "'Roboto', sans-serif",
              }}>
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Financing ──────────────────────────────────────────── */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container-xl" style={{ textAlign: 'center', maxWidth: '700px' }}>
          <span style={{ color: ELO_GOLD, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase' }} className="reveal">
            Financing Available
          </span>
          <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: '0.5rem', color: ELO_NAVY, textTransform: 'uppercase' }} className="reveal">
            Protect Your Home — Even on a Budget
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '1rem', marginBottom: '2rem', marginTop: '0.75rem' }} className="reveal">
            We partner with GreenSky and Synchrony to offer flexible financing. Apply in minutes — most decisions are instant. $0 down, 0% interest for 18 months for qualified customers.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1.25rem', marginBottom: '2.5rem' }}>
            {[
              { icon: '💰', label: '$0 Down', sub: 'No upfront cost' },
              { icon: '0%', label: '0% Interest', sub: '18-month promo' },
              { icon: '⚡', label: 'Instant Decision', sub: 'Apply online' },
              { icon: '📅', label: 'Up to 60 Months', sub: 'Flexible terms' },
            ].map((item, i) => (
              <div key={item.label} className={`card reveal-scale reveal-delay-${i + 1}`} style={{ textAlign: 'center', border: `1px solid rgba(30,30,87,0.1)` }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{item.icon}</div>
                <div style={{ fontWeight: 800, color: ELO_NAVY, fontSize: '1.1rem' }}>{item.label}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{item.sub}</div>
              </div>
            ))}
          </div>
          <Link to={ROUTES.FINANCING} className="btn-primary">Check Your Rate <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
